import { useState, useEffect } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import StaffRewardImageUpload from '../components/StaffRewardImageUpload';
import StaffRewardTile from '../components/StaffRewardTile';
import '../styles/StaffPages.css';
import xmarkIcon from '../assets/icons/xmark.svg';
import apiService from '../services/api';

// The moment a reward counts down to: an auction's close, a raffle's drawing.
// Plain checkout rewards have no such date, so they sort to the bottom.
function rewardMilestoneTime(reward) {
  const raw =
    reward.redemption_type === 'auction'
      ? reward.auction_info?.end_date
      : reward.redemption_type === 'raffle'
        ? reward.raffle_info?.drawing_date
        : null;
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? null : time;
}

function compareByMilestone(a, b, newestFirst) {
  const ta = rewardMilestoneTime(a);
  const tb = rewardMilestoneTime(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1; // undated always last, in both directions
  if (tb === null) return -1;
  return newestFirst ? tb - ta : ta - tb;
}

// Soonest upcoming first.
const bySoonestFirst = (a, b) => compareByMilestone(a, b, false);
// For finished rewards "soonest" is meaningless — show the most recent instead.
const byMostRecentFirst = (a, b) => compareByMilestone(a, b, true);

const StaffRewards = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    redemptionType: 'checkout',
    category: '',
    points: '',
    cashPrice: '',
    drawingDate: '',
    drawingTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    startingBid: '',
    minimumIncrement: '1',
    images: [],
    giftCardCodes: [],
    isFeatured: false
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    redemptionType: 'checkout',
    category: '',
    points: '',
    cashPrice: '',
    drawingDate: '',
    drawingTime: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    startingBid: '',
    minimumIncrement: '1',
    images: [],
    existingImages: [],
    giftCardCodes: [],
    isFeatured: false
  });
  const [toast, setToast] = useState(null);

  const redemptionTypes = [
    { value: 'checkout', label: 'Checkout (default)' },
    { value: 'card', label: 'Card (USD only)' },
    { value: 'raffle', label: 'Raffle' },
    { value: 'auction', label: 'Auction' }
  ];

  const categories = [
    'Merchandise',
    'Apparel',
    'Experience',
    'Collectibles',
    'Gift Card'
  ];

  useEffect(() => {
    fetchRewards();
  }, []);

  useEffect(() => {
    if (formData.category !== 'Gift Card') return;
    setFormData(prev => ({
      ...prev,
      points: '0',
      redemptionType: 'checkout',
      cashPrice: '',
    }));
  }, [formData.category]);

  useEffect(() => {
    if (editFormData.category !== 'Gift Card') return;
    setEditFormData(prev => ({
      ...prev,
      points: '0',
      redemptionType: 'checkout',
      cashPrice: '',
    }));
  }, [editFormData.category]);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const rewardsData = await apiService.getRewards();
      setRewards(rewardsData.results || rewardsData || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // Convert S3 path to HTTPS URL if needed
    if (imagePath.startsWith('s3://')) {
      return imagePath.replace('s3://', 'https://');
    }

    // If it's already a full URL, return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // If it's just a filename (like "me.jpeg"), it's likely corrupted data
    // Return null to hide these broken images
    if (!imagePath.includes('/')) {
      return null;
    }

    return imagePath;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addGiftCardCode = () => {
    setFormData(prev => ({
      ...prev,
      giftCardCodes: [...prev.giftCardCodes, '']
    }));
  };

  const updateGiftCardCode = (index, value) => {
    setFormData(prev => ({
      ...prev,
      giftCardCodes: prev.giftCardCodes.map((code, idx) => (idx === index ? value : code))
    }));
  };

  const removeGiftCardCode = (index) => {
    setFormData(prev => ({
      ...prev,
      giftCardCodes: prev.giftCardCodes.filter((_, idx) => idx !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Validate form data
      if (!formData.name.trim()) {
        alert('Please enter a reward name');
        return;
      }

      if (!formData.description.trim()) {
        alert('Please enter a description');
        return;
      }

      if (!formData.redemptionType) {
        alert('Please select a redemption type');
        return;
      }

      if (!formData.category) {
        alert('Please select a category');
        return;
      }

      if (
        formData.redemptionType !== 'auction' &&
        formData.redemptionType !== 'card' &&
        formData.category !== 'Gift Card' &&
        (!formData.points || parseInt(formData.points) <= 0)
      ) {
        alert('Please enter a valid points amount');
        return;
      }

      if (formData.redemptionType === 'card') {
        if (!formData.cashPrice || String(formData.cashPrice).trim() === '') {
          alert('Enter the price in USD for card-only rewards');
          return;
        }
        const usdCard = parseFloat(formData.cashPrice);
        if (Number.isNaN(usdCard) || usdCard < 0.5) {
          alert('Card price must be at least $0.50');
          return;
        }
      }

      if (formData.redemptionType === 'raffle') {
        if ((formData.startDate && !formData.startTime) || (!formData.startDate && formData.startTime)) {
          alert('Please select both an unlock date and time for raffle rewards');
          return;
        }
        if (!formData.drawingDate || !formData.drawingTime) {
          alert('Please select both a drawing date and time for raffle rewards');
          return;
        }

        const raffleDrawingDateTime = new Date(`${formData.drawingDate}T${formData.drawingTime}`);
        if (formData.startDate && formData.startTime) {
          const raffleStartDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
          if (raffleDrawingDateTime <= raffleStartDateTime) {
            alert('Raffle drawing date/time must be after unlock date/time');
            return;
          }
        }
      }

      if (formData.redemptionType === 'auction') {
        if (!formData.startDate || !formData.startTime) {
          alert('Please select both a start date and time for auction rewards');
          return;
        }
        if (!formData.endDate || !formData.endTime) {
          alert('Please select both an end date and time for auction rewards');
          return;
        }
        if (!formData.startingBid || parseFloat(formData.startingBid) <= 0) {
          alert('Please enter a valid starting bid amount');
          return;
        }
        if (!formData.minimumIncrement || parseFloat(formData.minimumIncrement) <= 0) {
          alert('Please enter a valid minimum increment');
          return;
        }

        // Check that end time is after start time
        const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
        const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
        if (endDateTime <= startDateTime) {
          alert('Auction end date/time must be after start date/time');
          return;
        }
      }


      // Submit the reward
      const createPayload = {
        ...formData,
        giftCardCodes:
          formData.category === 'Gift Card'
            ? formData.giftCardCodes.map(code => code.trim()).filter(Boolean)
            : [],
      };
      await apiService.createReward(createPayload, formData.images);
      
      // Reset form and close
      handleCancel();
      // Refresh the rewards list
      fetchRewards();
      showToast('Reward created successfully!');

    } catch (error) {
      showToast('Error creating reward: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      redemptionType: 'checkout',
      category: '',
      points: '',
      cashPrice: '',
      drawingDate: '',
      drawingTime: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      startingBid: '',
      minimumIncrement: '1',
      images: [],
      giftCardCodes: [],
      isFeatured: false
    });
    setShowAddForm(false);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEditReward = (reward) => {
    setEditingReward(reward);

    // Convert reward_type back to the proper category format
    const categoryValue = reward.reward_type
      ? reward.reward_type
          .split('_')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ')
      : '';

    // Parse existing drawing date and time if available (convert from UTC to local timezone)
    let drawingDate = '';
    let drawingTime = '';
    if (reward.raffle_info?.drawing_date) {
      const dateTime = new Date(reward.raffle_info.drawing_date);
      // Convert to local timezone for display
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, '0');
      const day = String(dateTime.getDate()).padStart(2, '0');
      const hours = String(dateTime.getHours()).padStart(2, '0');
      const minutes = String(dateTime.getMinutes()).padStart(2, '0');

      drawingDate = `${year}-${month}-${day}`;
      drawingTime = `${hours}:${minutes}`;
    }

    // Parse existing raffle/auction dates and times if available (convert from UTC to local timezone)
    let startDate = '';
    let startTime = '';
    let endDate = '';
    let endTime = '';
    let startingBid = '';
    let minimumIncrement = '1';

    if (reward.raffle_info?.start_date) {
      const raffleStartDateTime = new Date(reward.raffle_info.start_date);
      const year = raffleStartDateTime.getFullYear();
      const month = String(raffleStartDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(raffleStartDateTime.getDate()).padStart(2, '0');
      const hours = String(raffleStartDateTime.getHours()).padStart(2, '0');
      const minutes = String(raffleStartDateTime.getMinutes()).padStart(2, '0');

      startDate = `${year}-${month}-${day}`;
      startTime = `${hours}:${minutes}`;
    }

    if (reward.auction_info?.start_date) {
      const startDateTime = new Date(reward.auction_info.start_date);
      const year = startDateTime.getFullYear();
      const month = String(startDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(startDateTime.getDate()).padStart(2, '0');
      const hours = String(startDateTime.getHours()).padStart(2, '0');
      const minutes = String(startDateTime.getMinutes()).padStart(2, '0');

      startDate = `${year}-${month}-${day}`;
      startTime = `${hours}:${minutes}`;
    }

    if (reward.auction_info?.end_date) {
      const endDateTime = new Date(reward.auction_info.end_date);
      const year = endDateTime.getFullYear();
      const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(endDateTime.getDate()).padStart(2, '0');
      const hours = String(endDateTime.getHours()).padStart(2, '0');
      const minutes = String(endDateTime.getMinutes()).padStart(2, '0');

      endDate = `${year}-${month}-${day}`;
      endTime = `${hours}:${minutes}`;
    }

    if (reward.auction_info?.starting_bid) {
      startingBid = reward.auction_info.starting_bid.toString();
    }

    if (reward.auction_info?.minimum_increment) {
      minimumIncrement = reward.auction_info.minimum_increment.toString();
    }

    const formData = {
      name: reward.title || '',
      description: reward.description || '',
      redemptionType: reward.redemption_type || 'checkout',
      category: categoryValue,
      points: reward.points_cost ? reward.points_cost.toString() : '',
      cashPrice:
        reward.cash_price_cents != null && reward.cash_price_cents !== ''
          ? (Number(reward.cash_price_cents) / 100).toFixed(2)
          : '',
      drawingDate: drawingDate,
      drawingTime: drawingTime,
      startDate: startDate,
      startTime: startTime,
      endDate: endDate,
      endTime: endTime,
      startingBid: startingBid,
      minimumIncrement: minimumIncrement,
      images: [],
      existingImages: reward.images || [],
      giftCardCodes: Array.isArray(reward.gift_card_codes) ? reward.gift_card_codes : [],
      isFeatured: !!reward.is_featured
    };
    console.log('Setting edit form data:', formData);
    console.log('Original reward data:', reward);
    setEditFormData(formData);
    setShowEditForm(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addEditGiftCardCode = () => {
    setEditFormData(prev => ({
      ...prev,
      giftCardCodes: [...prev.giftCardCodes, '']
    }));
  };

  const updateEditGiftCardCode = (index, value) => {
    setEditFormData(prev => ({
      ...prev,
      giftCardCodes: prev.giftCardCodes.map((code, idx) => (idx === index ? value : code))
    }));
  };

  const removeEditGiftCardCode = (index) => {
    setEditFormData(prev => ({
      ...prev,
      giftCardCodes: prev.giftCardCodes.filter((_, idx) => idx !== index)
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Validate form data
      if (!editFormData.name.trim()) {
        alert('Please enter a reward name');
        return;
      }

      if (!editFormData.description.trim()) {
        alert('Please enter a description');
        return;
      }

      if (!editFormData.redemptionType) {
        alert('Please select a redemption type');
        return;
      }

      if (!editFormData.category) {
        alert('Please select a category');
        return;
      }

      if (
        editFormData.redemptionType !== 'auction' &&
        editFormData.redemptionType !== 'card' &&
        editFormData.category !== 'Gift Card' &&
        (!editFormData.points || parseInt(editFormData.points) <= 0)
      ) {
        alert('Please enter a valid points amount');
        return;
      }

      if (editFormData.redemptionType === 'card') {
        if (!editFormData.cashPrice || String(editFormData.cashPrice).trim() === '') {
          alert('Enter the price in USD for card-only rewards');
          return;
        }
        const usdCard = parseFloat(editFormData.cashPrice);
        if (Number.isNaN(usdCard) || usdCard < 0.5) {
          alert('Card price must be at least $0.50');
          return;
        }
      }

      if (editFormData.redemptionType === 'raffle') {
        if ((editFormData.startDate && !editFormData.startTime) || (!editFormData.startDate && editFormData.startTime)) {
          alert('Please select both an unlock date and time for raffle rewards');
          return;
        }
        if (!editFormData.drawingDate || !editFormData.drawingTime) {
          alert('Please select both a drawing date and time for raffle rewards');
          return;
        }

        const raffleDrawingDateTime = new Date(`${editFormData.drawingDate}T${editFormData.drawingTime}`);
        if (editFormData.startDate && editFormData.startTime) {
          const raffleStartDateTime = new Date(`${editFormData.startDate}T${editFormData.startTime}`);
          if (raffleDrawingDateTime <= raffleStartDateTime) {
            alert('Raffle drawing date/time must be after unlock date/time');
            return;
          }
        }
      }

      if (editFormData.redemptionType === 'auction') {
        if (!editFormData.startDate || !editFormData.startTime) {
          alert('Please select both a start date and time for auction rewards');
          return;
        }
        if (!editFormData.endDate || !editFormData.endTime) {
          alert('Please select both an end date and time for auction rewards');
          return;
        }
        if (!editFormData.startingBid || parseFloat(editFormData.startingBid) <= 0) {
          alert('Please enter a valid starting bid amount');
          return;
        }
        if (!editFormData.minimumIncrement || parseFloat(editFormData.minimumIncrement) <= 0) {
          alert('Please enter a valid minimum increment');
          return;
        }

        // Check that end time is after start time
        const startDateTime = new Date(`${editFormData.startDate}T${editFormData.startTime}`);
        const endDateTime = new Date(`${editFormData.endDate}T${editFormData.endTime}`);
        if (endDateTime <= startDateTime) {
          alert('Auction end date/time must be after start date/time');
          return;
        }
      }

      // First, update the text fields
      const updateData = {
        title: editFormData.name,
        description: editFormData.description,
        redemption_type: editFormData.redemptionType,
        reward_type: editFormData.category.toLowerCase().replace(/\s+/g, '_'),
        gift_card_codes:
          editFormData.category === 'Gift Card'
            ? editFormData.giftCardCodes.map(code => code.trim()).filter(Boolean)
            : [],
        is_featured: !!editFormData.isFeatured,
      };

      // Only add points_cost for non-auction rewards (card-only uses 0 points)
      if (editFormData.redemptionType !== 'auction') {
        updateData.points_cost =
          editFormData.redemptionType === 'card' ? 0 : parseInt(editFormData.points, 10);
      }

      if (editFormData.redemptionType === 'card') {
        if (editFormData.cashPrice && String(editFormData.cashPrice).trim() !== '') {
          updateData.cash_price_cents = Math.round(parseFloat(editFormData.cashPrice) * 100);
        }
      } else if (editFormData.redemptionType === 'checkout') {
        updateData.cash_price_cents = null;
      }

      // Add drawing date if this is a raffle (convert local time to UTC)
      if (editFormData.redemptionType === 'raffle' && editFormData.drawingDate && editFormData.drawingTime) {
        const localDateTime = new Date(`${editFormData.drawingDate}T${editFormData.drawingTime}`);
        updateData.drawing_date = localDateTime.toISOString();
      }

      if (editFormData.redemptionType === 'raffle' && editFormData.startDate && editFormData.startTime) {
        const raffleStartDateTime = new Date(`${editFormData.startDate}T${editFormData.startTime}`);
        updateData.start_date = raffleStartDateTime.toISOString();
      }

      // Add auction fields if this is an auction (convert local time to UTC)
      if (editFormData.redemptionType === 'auction') {
        if (editFormData.startDate && editFormData.startTime) {
          const startDateTime = new Date(`${editFormData.startDate}T${editFormData.startTime}`);
          updateData.start_date = startDateTime.toISOString();
        }
        if (editFormData.endDate && editFormData.endTime) {
          const endDateTime = new Date(`${editFormData.endDate}T${editFormData.endTime}`);
          updateData.end_date = endDateTime.toISOString();
        }
        if (editFormData.startingBid) {
          updateData.starting_bid = parseFloat(editFormData.startingBid);
        }
        if (editFormData.minimumIncrement) {
          updateData.minimum_increment = parseFloat(editFormData.minimumIncrement);
        }
      }
      console.log('Updating reward with data:', updateData);
      console.log('Calling updateReward for ID:', editingReward.id);
      const result = await apiService.updateReward(editingReward.id, updateData);
      console.log('Update result:', result);

      // Second, check for removed images and delete them
      const originalImageIds = editingReward.images.map(img => img.id);
      const remainingImageIds = editFormData.existingImages.map(img => img.id);
      const removedImageIds = originalImageIds.filter(id => !remainingImageIds.includes(id));

      if (removedImageIds.length > 0) {
        await apiService.removeImagesFromReward(editingReward.id, removedImageIds);
      }

      // Third, if there are new images, add them
      if (editFormData.images && editFormData.images.length > 0) {
        await apiService.addImagesToReward(
          editingReward.id,
          editFormData.images
        );
      }

      handleEditCancel();
      fetchRewards();
      showToast('Reward updated successfully!');

    } catch (error) {
      showToast('Error updating reward: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditFormData({
      name: '',
      description: '',
      redemptionType: 'checkout',
      category: '',
      points: '',
      cashPrice: '',
      drawingDate: '',
      drawingTime: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      startingBid: '',
      minimumIncrement: '1',
      images: [],
      existingImages: [],
      giftCardCodes: [],
      isFeatured: false
    });
    setShowEditForm(false);
    setEditingReward(null);
  };

  const removeExistingImage = (index) => {
    setEditFormData(prev => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, i) => i !== index)
    }));
  };

  const handleToggleRewardStatus = async (rewardId, isActive) => {
    try {
      await apiService.toggleRewardStatus(rewardId, isActive);
      // Refresh the rewards list to show updated status
      await fetchRewards();
    } catch (error) {
      showToast('Error updating reward status: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleDeleteReward = async (rewardId) => {
    try {
      await apiService.deleteReward(rewardId);
      setRewards(prev => prev.filter(r => r.id !== rewardId));
      showToast('Reward deleted', 'success');
    } catch (error) {
      showToast('Error deleting reward: ' + (error.response?.data?.message || error.message), 'error');
    }
  };








  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="rewards" />
      <div className="admin-main">
        <div className="admin-content">
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Reward Management</h1>
              <p className="staff-page-subheader">Manage rewards and redemptions for your school.</p>
            </div>
            {/* Same ghost button the first-rewards editor uses. */}
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontFamily: '"Baikal Book"',
                fontSize: '13px',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              + Add reward
            </button>
          </div>

          {showAddForm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }} onClick={handleCancel}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '0',
                width: '640px',
                maxWidth: '92%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }} onClick={(e) => e.stopPropagation()}>

            <div className="add-reward-section" style={{ margin: 0 }}>
              <h1 className="staff-page-header">Add New Reward</h1>
              <p className="staff-page-subheader">Create a new reward for students to redeem with their points</p>

              <form onSubmit={handleSubmit} className="reward-form reward-form--compact">
                <div className="form-group">
                  <label className="form-label">Reward Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter reward name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Describe the reward..."
                    rows="4"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Redemption Type</label>
                    <select
                      name="redemptionType"
                      value={formData.redemptionType}
                      onChange={handleInputChange}
                      className="form-select"
                      disabled={formData.category === 'Gift Card'}
                      required
                    >
                      {redemptionTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Points Required — not for auction or card-only */}
                  {formData.category !== 'Gift Card' && formData.redemptionType !== 'auction' && formData.redemptionType !== 'card' && (
                    <div className="form-group">
                      <label className="form-label">Points Required</label>
                      <input
                        type="number"
                        name="points"
                        value={formData.points}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter points needed"
                        min="1"
                        required
                      />
                    </div>
                  )}
                </div>

                {formData.category !== 'Gift Card' && formData.redemptionType === 'card' && (
                  <div className="form-group">
                    <label className="form-label">Price (USD)</label>
                    <input
                      type="number"
                      name="cashPrice"
                      value={formData.cashPrice}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g. 9.99 (min 0.50)"
                      min="0.5"
                      step="0.01"
                      required
                    />
                  </div>
                )}

                {/* Raffle date fields - only show if redemption type is raffle */}
                {formData.category !== 'Gift Card' && formData.redemptionType === 'raffle' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Raffle Unlock Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Default: 1 week before drawing"
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="startTime"
                            value={formData.startTime}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Default: drawing time"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Raffle Drawing Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="drawingDate"
                            value={formData.drawingDate}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="drawingTime"
                            value={formData.drawingTime}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select time"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Auction fields - only show if redemption type is auction */}
                {formData.category !== 'Gift Card' && formData.redemptionType === 'auction' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Auction Start Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select start date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="startTime"
                            value={formData.startTime}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select start time"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Auction End Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select end date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="endTime"
                            value={formData.endTime}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Select end time"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Auction Settings</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                            Starting Bid (Points)
                          </label>
                          <input
                            type="number"
                            name="startingBid"
                            value={formData.startingBid}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="e.g. 100"
                            min="1"
                            step="1"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                            Minimum Increment (Points)
                          </label>
                          <input
                            type="number"
                            name="minimumIncrement"
                            value={formData.minimumIncrement}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="e.g. 1"
                            min="0.01"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {formData.category === 'Gift Card' && (
                  <div className="form-group">
                    <label className="form-label">Gift Card Codes</label>
                    {formData.giftCardCodes.length === 0 && (
                      <div style={{ color: '#6b7280', marginBottom: '10px' }}>No codes added yet.</div>
                    )}
                    {formData.giftCardCodes.map((code, index) => (
                      <div key={`gift-card-code-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => updateGiftCardCode(index, e.target.value)}
                          className="form-input"
                          placeholder={`Code ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={() => removeGiftCardCode(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" className="staff-page-button" onClick={addGiftCardCode}>
                      <span className="staff-page-button-text">Add code</span>
                    </button>
                  </div>
                )}

                <StaffRewardImageUpload
                  images={formData.images}
                  setImages={updater =>
                    setFormData(prev => ({
                      ...prev,
                      images: typeof updater === 'function' ? updater(prev.images) : updater,
                    }))
                  }
                  inputId="reward-create-image-upload"
                />

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={formData.isFeatured}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))
                      }
                    />
                    Feature this reward
                  </label>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#666' }}>
                    Featured rewards are pinned to the top of the rewards page and removed
                    from the main list below it.
                  </p>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="staff-page-button submit-button" disabled={isSubmitting}>
                    <span className="staff-page-button-text">
                      {isSubmitting ? 'Creating...' : 'Create Reward'}
                    </span>
                  </button>
                </div>
              </form>
            </div>
              </div>
            </div>
          )}

          {/* Active Rewards Section */}
          {!loading && rewards.filter(reward => {
            if (!reward.is_active) return false;

            const now = new Date();

            // Check if auction has ended
            if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
              const endDate = new Date(reward.auction_info.end_date);
              return endDate > now;
            }

            // Check if raffle drawing has passed
            if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
              const drawingDate = new Date(reward.raffle_info.drawing_date);
              return drawingDate > now;
            }

            // For regular checkout rewards, they're active if is_active is true
            return true;
          }).length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h2 style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                Active Rewards ({rewards.filter(reward => {
                  if (!reward.is_active) return false;

                  const now = new Date();

                  if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
                    const endDate = new Date(reward.auction_info.end_date);
                    return endDate > now;
                  }

                  if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
                    const drawingDate = new Date(reward.raffle_info.drawing_date);
                    return drawingDate > now;
                  }

                  return true;
                }).length})
              </h2>
              <div style={{
                display: 'grid',
                // Two per row, so each card sits at half the page width.
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '16px',
                alignItems: 'start',
                marginBottom: '2rem'
              }}>
                {rewards.filter(reward => {
                  if (!reward.is_active) return false;

                  const now = new Date();

                  if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
                    const endDate = new Date(reward.auction_info.end_date);
                    return endDate > now;
                  }

                  if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
                    const drawingDate = new Date(reward.raffle_info.drawing_date);
                    return drawingDate > now;
                  }

                  return true;
                }).sort(bySoonestFirst).map((reward) => (
                  <StaffRewardTile
                    key={reward.id}
                    reward={reward}
                    getImageUrl={getImageUrl}
                    isActive={true}
                    onToggleStatus={handleToggleRewardStatus}
                    onEdit={handleEditReward}
                    onDelete={handleDeleteReward}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Completed Rewards Section */}
          {!loading && rewards.filter(reward => {
            if (!reward.is_active) return false;

            const now = new Date();

            // Check if auction has ended
            if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
              const endDate = new Date(reward.auction_info.end_date);
              return endDate <= now;
            }

            // Check if raffle drawing has passed
            if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
              const drawingDate = new Date(reward.raffle_info.drawing_date);
              return drawingDate <= now;
            }

            return false;
          }).length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h2 style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                Completed Rewards ({rewards.filter(reward => {
                  if (!reward.is_active) return false;

                  const now = new Date();

                  if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
                    const endDate = new Date(reward.auction_info.end_date);
                    return endDate <= now;
                  }

                  if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
                    const drawingDate = new Date(reward.raffle_info.drawing_date);
                    return drawingDate <= now;
                  }

                  return false;
                }).length})
              </h2>
              <div style={{
                display: 'grid',
                // Two per row, so each card sits at half the page width.
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '16px',
                alignItems: 'start',
                marginBottom: '2rem'
              }}>
                {rewards.filter(reward => {
                  if (!reward.is_active) return false;

                  const now = new Date();

                  if (reward.redemption_type === 'auction' && reward.auction_info?.end_date) {
                    const endDate = new Date(reward.auction_info.end_date);
                    return endDate <= now;
                  }

                  if (reward.redemption_type === 'raffle' && reward.raffle_info?.drawing_date) {
                    const drawingDate = new Date(reward.raffle_info.drawing_date);
                    return drawingDate <= now;
                  }

                  return false;
                }).sort(byMostRecentFirst).map((reward) => (
                  <StaffRewardTile
                    key={reward.id}
                    reward={reward}
                    getImageUrl={getImageUrl}
                    isActive={true}
                    onToggleStatus={null}
                    onEdit={handleEditReward}
                    onDelete={handleDeleteReward}
                    isCompleted={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive Rewards Section */}
          {!loading && rewards.filter(reward => !reward.is_active).length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h2 style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                Inactive Rewards ({rewards.filter(reward => !reward.is_active).length})
              </h2>
              <div style={{
                display: 'grid',
                // Two per row, so each card sits at half the page width.
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '16px',
                alignItems: 'start',
                marginBottom: '2rem'
              }}>
                {rewards.filter(reward => !reward.is_active).sort(bySoonestFirst).map((reward) => (
                  <StaffRewardTile
                    key={reward.id}
                    reward={reward}
                    getImageUrl={getImageUrl}
                    isActive={false}
                    onToggleStatus={handleToggleRewardStatus}
                    onEdit={handleEditReward}
                    onDelete={handleDeleteReward}
                  />
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading rewards...
            </div>
          )}

          {!loading && rewards.length === 0 && (
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '3rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#666',
              marginTop: '2rem'
            }}>
              <h3>No rewards created yet</h3>
              <p>Create your first reward using the "Add Reward" button above.</p>
            </div>
          )}
        </div>
      </div>

      {/* Update Reward Modal */}
      {showEditForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }} onClick={handleEditCancel}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '0',
            // Matches the first-rewards editor modal.
            width: '640px',
            maxWidth: '92%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }} onClick={(e) => e.stopPropagation()}>

            <div className="add-reward-section" style={{ margin: 0 }}>
              <h1 className="staff-page-header">Update Reward</h1>
              <p className="staff-page-subheader">Update reward details and settings</p>

              <form onSubmit={handleEditSubmit} className="reward-form reward-form--compact">
                <div className="form-group">
                  <label className="form-label">Reward Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    className="form-input"
                    placeholder="Enter reward name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={editFormData.description}
                    onChange={handleEditInputChange}
                    className="form-textarea"
                    placeholder="Describe the reward..."
                    rows="4"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Redemption Type</label>
                    <select
                      name="redemptionType"
                      value={editFormData.redemptionType}
                      onChange={handleEditInputChange}
                      className="form-select"
                      disabled={editFormData.category === 'Gift Card'}
                      required
                    >
                      {redemptionTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Points Required — not for auction or card-only */}
                  {editFormData.category !== 'Gift Card' && editFormData.redemptionType !== 'auction' && editFormData.redemptionType !== 'card' && (
                    <div className="form-group">
                      <label className="form-label">Points Required</label>
                      <input
                        type="number"
                        name="points"
                        value={editFormData.points}
                        onChange={handleEditInputChange}
                        className="form-input"
                        placeholder="Enter points needed"
                        min="1"
                        required
                      />
                    </div>
                  )}
                </div>

                {editFormData.category !== 'Gift Card' && editFormData.redemptionType === 'card' && (
                  <div className="form-group">
                    <label className="form-label">Price (USD)</label>
                    <input
                      type="number"
                      name="cashPrice"
                      value={editFormData.cashPrice}
                      onChange={handleEditInputChange}
                      className="form-input"
                      placeholder="e.g. 9.99 (min 0.50)"
                      min="0.5"
                      step="0.01"
                      required
                    />
                  </div>
                )}

                {/* Raffle date fields - only show if redemption type is raffle */}
                {editFormData.category !== 'Gift Card' && editFormData.redemptionType === 'raffle' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Raffle Unlock Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="startDate"
                            value={editFormData.startDate}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Default: 1 week before drawing"
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="startTime"
                            value={editFormData.startTime}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Default: drawing time"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Raffle Drawing Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="drawingDate"
                            value={editFormData.drawingDate}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="drawingTime"
                            value={editFormData.drawingTime}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select time"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Auction fields - only show if redemption type is auction */}
                {editFormData.category !== 'Gift Card' && editFormData.redemptionType === 'auction' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Auction Start Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="startDate"
                            value={editFormData.startDate}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select start date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="startTime"
                            value={editFormData.startTime}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select start time"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Auction End Date & Time</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <input
                            type="date"
                            name="endDate"
                            value={editFormData.endDate}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select end date"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <input
                            type="time"
                            name="endTime"
                            value={editFormData.endTime}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="Select end time"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Auction Settings</label>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                            Starting Bid (Points)
                          </label>
                          <input
                            type="number"
                            name="startingBid"
                            value={editFormData.startingBid}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="e.g. 100"
                            min="1"
                            step="1"
                            required
                          />
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                            Minimum Increment (Points)
                          </label>
                          <input
                            type="number"
                            name="minimumIncrement"
                            value={editFormData.minimumIncrement}
                            onChange={handleEditInputChange}
                            className="form-input"
                            placeholder="e.g. 1"
                            min="0.01"
                            step="0.01"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={editFormData.category}
                    onChange={handleEditInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {editFormData.category === 'Gift Card' && (
                  <div className="form-group">
                    <label className="form-label">Gift Card Codes</label>
                    {editFormData.giftCardCodes.length === 0 && (
                      <div style={{ color: '#6b7280', marginBottom: '10px' }}>No codes added yet.</div>
                    )}
                    {editFormData.giftCardCodes.map((code, index) => (
                      <div key={`edit-gift-card-code-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => updateEditGiftCardCode(index, e.target.value)}
                          className="form-input"
                          placeholder={`Code ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={() => removeEditGiftCardCode(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" className="staff-page-button" onClick={addEditGiftCardCode}>
                      <span className="staff-page-button-text">Add code</span>
                    </button>
                  </div>
                )}

                <StaffRewardImageUpload
                  images={editFormData.images}
                  setImages={updater =>
                    setEditFormData(prev => ({
                      ...prev,
                      images: typeof updater === 'function' ? updater(prev.images) : updater,
                    }))
                  }
                  inputId="reward-edit-image-upload"
                  maxImages={Math.max(0, 6 - editFormData.existingImages.length)}
                  extraPreviewItems={
                    <>
                      {editFormData.existingImages.map((image, index) => (
                        <div key={`existing-${index}`} className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <img
                              src={getImageUrl(image.image_path)}
                              alt={`Existing ${index + 1}`}
                              className="preview-image"
                            />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeExistingImage(index)}
                            >
                              <img src={xmarkIcon} alt="Remove" />
                            </button>
                          </div>
                          <div className="preview-name">Existing Image {index + 1}</div>
                        </div>
                      ))}
                    </>
                  }
                />

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={editFormData.isFeatured}
                      onChange={e =>
                        setEditFormData(prev => ({ ...prev, isFeatured: e.target.checked }))
                      }
                    />
                    Feature this reward
                  </label>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#666' }}>
                    Featured rewards are pinned to the top of the rewards page and removed
                    from the main list below it.
                  </p>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleEditCancel}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="staff-page-button submit-button" disabled={isSubmitting}>
                    <span className="staff-page-button-text">
                      {isSubmitting ? 'Updating...' : 'Update Reward'}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toast.type === 'error' ? '#EF4444' : '#10B981',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1001,
          fontSize: '0.875rem',
          fontWeight: '500',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {toast.message}
          <button
            onClick={() => setToast(null)}
            style={{
              marginLeft: '12px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default StaffRewards;